import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pickaxe, Hammer, Truck, LayoutGrid, Trees, Droplets } from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const ICONS = { Pickaxe, Hammer, Truck, LayoutGrid, Trees, Droplets };

export default function Services() {
  usePageTitle("Services", "Excavation, concrete foundations, site prep, decorative concrete, retaining walls, driveways and drainage — seven service lines from one accountable BC crew.");
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  return (
    <div className="pt-20 md:pt-24" data-testid="services-page">
      <section className="bg-[#2C3539] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase mb-3">Services</div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.92]">Excavation, concrete<br/>& site work.</h1>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              From residential foundations through heavy-civil and commercial packages — six core service lines,
              one accountable crew. Every project starts with a free written estimate.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#2C3539]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 px-5 md:px-10 lg:px-16 pb-20 md:pb-28">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Hammer;
            return (
              <Link
                to={`/services/${s.slug}`}
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className="bg-[#2C3539] hover:bg-[#2F5F80] text-white group p-8 md:p-10 transition-colors min-h-[260px] flex flex-col"
              >
                <Icon size={40} className="text-[#9CC4DB] group-hover:text-white transition-colors" />
                <div className="font-display text-3xl md:text-4xl uppercase mt-5">{s.title}</div>
                {s.subtitle && <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#9CC4DB] group-hover:text-white/90 mt-1.5" data-testid={`service-subtitle-${s.slug}`}>{s.subtitle}</div>}
                <p className="mt-3 text-white/75 group-hover:text-white text-sm md:text-base flex-1">{s.blurb}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase">
                  Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-24" data-testid="services-process">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">How We Work</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase leading-[1] text-[#23282B]">From first call<br/>to final grade.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Site Visit & Quote", b: "We walk the site with you, talk through scope and access, and deliver a free written estimate within 48 hours — no vague allowances, no surprises buried in the fine print." },
              { n: "02", t: "Plan & Permits", b: "Before a machine arrives we handle BC One Call utility locates, coordinate with your engineer where required, and make sure municipal permits and inspections are lined up." },
              { n: "03", t: "The Work", b: "Our own crew self-performs the excavation, concrete and site work — the same people who quoted the job are the ones doing it, keeping schedule and quality under one roof." },
              { n: "04", t: "Walkthrough & Cleanup", b: "We finish with a final grade check and a full walkthrough, and we leave the site clean. Our workmanship stands behind every pour, wall and drain we install." },
            ].map((s) => (
              <div key={s.n} className="border-t-4 border-[#2F5F80] bg-[#F1F3F5] p-6" data-testid={`process-step-${s.n}`}>
                <div className="font-display text-4xl text-[#2F5F80]/40">{s.n}</div>
                <div className="font-display text-2xl uppercase mt-2 text-[#23282B]">{s.t}</div>
                <p className="mt-2 text-sm text-[#23282B]/75 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-[#23282B]/75 text-base leading-relaxed">
            Whether it's a tight residential lot in Langford, a drainage retrofit in Victoria, or a commercial site package on the
            Lower Mainland, the process is the same: honest scope, clean execution, and a finished product built for BC's climate.
            Assessments are always free — send photos through the contact form or call <a href="tel:+12508861112" className="text-[#2F5F80] font-semibold">(250) 886-1112</a>.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <img
              src="https://customer-assets.emergentagent.com/job_av-job-command/artifacts/6emprgua_481445349_1077750924155880_2142199960928470481_n.jpg"
              alt="Stamped concrete"
              className="w-full h-[460px] object-cover"
            />
          </div>
          <div className="lg:col-span-5">
            <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">Finished Craftsmanship</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[1] text-[#23282B]">Decorative concrete<br/>that anchors the design.</h2>
            <p className="mt-5 text-[#23282B]/80 leading-relaxed">
              Stamped patios, exposed aggregate driveways, integral colour, and broom finishes — handled by the same crew that pours your foundation.
            </p>
            <Link to="/contact" data-testid="services-cta" className="mt-8 inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
